import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect } from 'react';
import AddTote from '@/pages/AddTote';
import { BulkMove, BulkStock } from '@/pages/BulkScan';
import ForgotPassword from '@/pages/ForgotPassword';
import SalesOrderConvertToSerializedPickScan from '@/pages/SalesOrderConvertToSerializedPickScan';
import SalesOrderDocuments from '@/pages/SalesOrderDocuments';
import SalesOrderPick from '@/pages/SalesOrderPick';
import SalesOrderPickScan from '@/pages/SalesOrderPickScan';
import SalesOrderPicking from '@/pages/SalesOrderPicking';
import SalesOrderVerify from '@/pages/SalesOrderVerify';
import SalesOrderShipping from '@/pages/SalesOrderShipping';
import SalesOrderSummary from '@/pages/SalesOrderSummary';
import SalesOrderPackages from '@/pages/SalesOrderPackages';
import SalesOrders from '@/pages/SalesOrders';
import Destroy from '@/pages/Destroy';
import DestroyUnserialized from '@/pages/DestroyUnserialized';
import Move from '@/pages/Move';
import MoveUnserialized from '@/pages/MoveUnserialized';
import UnserializedPurchaseOrders from '@/pages/UnserializedPurchaseOrders';
import Quarantine from '@/pages/Quarantine/';
import QuarantineUnserialized from '@/pages/QuarantineUnserialized';
import PurchaseOrderReceive from '@/pages/PurchaseOrderReceive';
import ReturnReceive from '@/pages/ReturnReceive';
import Scanner from '@/pages/Scanner';
import ScannedOptions from '@/pages/ScannedOptions';
import ScannedProduct from '@/pages/ScannedProduct';
import SignIn from '@/pages/SignIn';
import Stock from '@/pages/Stock';
import StockUnserialized from '@/pages/StockUnserialized';
import UnserializedReceiving from '@/pages/UnserializedReceiving';
import UnserializedReceivingScan from '@/pages/UnserializedReceivingScan';
import SalesOrderUnserializedPickScan from '@/pages/SalesOrderUnserializedPickScan';
import { ScannerProvider } from './com/ScannerProvider/ScannerProvider';
import UnserializedStocking from '@/pages/UnserializedStocking';
import PlaceholderPurchaseOrders from '@/pages/PlaceholderPurchaseOrders';
import PlaceholderReceiving from '@/pages/PlaceholderReceiving';
import PlaceholderStocking from '@/pages/PlaceholderStocking';
import PickPathSetup from '@/pages/PickPathSetup';
import QuarantineCleanup from '@/pages/QuarantineCleanup';
import ZoneCount from '@/pages/ZoneCount';

const ROUTES = [
  {
    path: '/add-tote',
    component: AddTote,
    authenticated: true
  },
  {
    path: '/reset-password',
    component: ForgotPassword,
    authenticated: false
  },
  {
    path: '/sales-order-convert-to-serialized-pick-scan',
    component: SalesOrderConvertToSerializedPickScan,
    authenticated: true
  },
  {
    path: '/sales-order-documents/:id',
    component: SalesOrderDocuments,
    authenticated: true
  },
  {
    path: '/sales-order-pick-scan',
    component: SalesOrderPickScan,
    authenticated: true
  },
  {
    path: '/sales-order-unserialized-pick-scan/:id',
    component: SalesOrderUnserializedPickScan,
    authenticated: true
  },
  {
    path: '/sales-order-picking/:id',
    component: SalesOrderPicking,
    authenticated: true
  },
  // Pick → Verify → Ship (Option 2). Reached only through pickVerifyRouteFor (Summary) — never by
  // the Summary's `/sales-order-${vendorStatus}/:id` rule, which stays the standard route — and
  // each screen guards itself: an order that is not Option 2 at the right stage is sent away.
  {
    path: '/sales-order-pick/:id',
    component: SalesOrderPick,
    authenticated: true
  },
  {
    path: '/sales-order-verify/:id',
    component: SalesOrderVerify,
    authenticated: true
  },
  {
    path: '/sales-order-shipping/:id',
    component: SalesOrderShipping,
    authenticated: true
  },
  // Box Sizes: correct the box size of a shipped order's packages. Reached from its Documents page.
  {
    path: '/sales-order-packages/:id',
    component: SalesOrderPackages,
    authenticated: true
  },
  {
    path: '/sales-order-summary/:id',
    component: SalesOrderSummary,
    authenticated: true
  },
  {
    path: '/sales-orders',
    component: SalesOrders,
    authenticated: true
  },
  {
    path: '/destroy',
    component: Destroy,
    authenticated: true
  },
  {
    path: '/destroy-unserialized',
    component: DestroyUnserialized,
    authenticated: true
  },
  {
    path: '/move',
    component: Move,
    authenticated: true
  },
  {
    path: '/move-unserialized',
    component: MoveUnserialized,
    authenticated: true
  },
  // Bulk Stock / Bulk Move: scan many units, cases or pallets, then one zone for all of them.
  // `?serialNumber=&gtin=` (from Scanned Options) starts the list with that item.
  {
    path: '/bulk-stock',
    component: BulkStock,
    authenticated: true
  },
  {
    path: '/bulk-move',
    component: BulkMove,
    authenticated: true
  },
  {
    path: '/unserialized',
    component: UnserializedPurchaseOrders,
    authenticated: true
  },
  {
    path: '/scanner',
    component: Scanner
    // authenticated: true
  },
  {
    path: '/scanned-options',
    component: ScannedOptions,
    authenticated: true
  },
  {
    path: '/scanned-product',
    component: ScannedProduct,
    authenticated: true
  },
  {
    path: '/stock',
    component: Stock,
    authenticated: true
  },
  {
    path: '/stock-unserialized',
    component: StockUnserialized,
    authenticated: true
  },
  {
    path: '/quarantine',
    component: Quarantine,
    authenticated: true
  },
  {
    path: '/quarantine-unserialized',
    component: QuarantineUnserialized,
    authenticated: true
  },
  {
    path: '/purchase-order-receive/:purchaseOrderId',
    component: PurchaseOrderReceive,
    authenticated: true
  },
  {
    // Opened by scanning the RMA # barcode on a return's RMA document.
    path: '/return-receive/:id',
    component: ReturnReceive,
    authenticated: true
  },
  {
    path: '/unserialized-receiving/:purchaseOrderId',
    component: UnserializedReceiving,
    authenticated: true
  },
  {
    path: '/unserialized-stocking/:purchaseOrderId',
    component: UnserializedStocking,
    authenticated: true
  },
  {
    path: '/placeholder-purchase-orders',
    component: PlaceholderPurchaseOrders,
    authenticated: true
  },
  {
    path: '/placeholder-receiving/:purchaseOrderId',
    component: PlaceholderReceiving,
    authenticated: true
  },
  {
    path: '/placeholder-stocking/:purchaseOrderId',
    component: PlaceholderStocking,
    authenticated: true
  },
  {
    path: '/pick-path-setup',
    component: PickPathSetup,
    authenticated: true
  },
  {
    path: '/quarantine-cleanup',
    component: QuarantineCleanup,
    authenticated: true
  },
  // `?zoneId=` opens straight onto a zone — a zone label scanned on the home screen lands here.
  {
    path: '/zone-count',
    component: ZoneCount,
    authenticated: true
  }
];

const getDefaultComponent = (loggedIn) => (loggedIn ? Scanner : SignIn);

const routes = ({ foundCurrentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const privateRoutes = ROUTES.filter(({ authenticated }) => !!authenticated);
  const publicRoutes = ROUTES.filter(({ authenticated }) => !authenticated);

  const loggedOutOnPrivatePath =
    !foundCurrentUser &&
    privateRoutes.some(({ path }) => {
      const [, firstPath] = path.split('/');
      return !!firstPath && pathname.includes(firstPath);
    });

  const DefaultComponent = getDefaultComponent(!!foundCurrentUser);

  useEffect(() => {
    if (loggedOutOnPrivatePath && pathname !== '/') navigate('/');
  }, [foundCurrentUser]);

  return (
    <div className="page-wrap">
      <ScannerProvider>
        <Routes>
          <Route path="/" element={<DefaultComponent />} />
          {!loggedOutOnPrivatePath ? (
            privateRoutes.map(({ path, component: Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))
          ) : (
            <Route path="/" element={<DefaultComponent />} />
          )}
          {publicRoutes.map(({ path, component: Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Routes>
      </ScannerProvider>
    </div>
  );
};

export default routes;
